import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import API_BASE from '../utils/api';
import useDocumentTitle from '../hooks/useDocumentTitle';

const ForgotPassword = () => {
  useDocumentTitle(
    'Reset Password | Teaching Hub School Management System',
    'Reset your Teaching Hub account password securely via email OTP verification.'
  );
  const [step, setStep] = useState(1); // 1=email, 2=otp, 3=new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hint, setHint] = useState('');
  const [schoolInfo, setSchoolInfo] = useState({ schoolName: '' });
  const [displayOtp, setDisplayOtp] = useState(''); // OTP shown on screen when no email configured
  const [noEmail, setNoEmail] = useState(false);

  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      switch(user.role) {
        case 'super_admin':
          navigate('/super-admin/dashboard');
          break;
        case 'admin':
          navigate('/admin/dashboard');
          break;
        case 'principal':
          navigate('/principal/dashboard');
          break;
        case 'class_teacher':
          navigate('/teacher/dashboard');
          break;
        case 'parent':
          navigate('/parent/dashboard');
          break;
        default:
          navigate('/');
      }
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    axios.get(`${API_BASE}/settings/public`)
      .then(res => setSchoolInfo(res.data))
      .catch(() => {});
  }, []);

  // Show loading state while auth context is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin mb-4">
            <div className="h-12 w-12 border-4 border-indigo-600 border-r-transparent rounded-full"></div>
          </div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/forgot-password`, { email });
      setHint(res.data.hint);
      setSuccess(res.data.message);
      if (!res.data.emailSent && res.data.otp) {
        setDisplayOtp(res.data.otp);
        setNoEmail(true);
      }
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/verify-otp`, { email, otp });
      setSuccess('OTP verified! Set your new password.');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid OTP');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/reset-password`, { email, otp, newPassword });
      setSuccess(res.data.message);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-blue-800 to-indigo-700">
        <div className="absolute top-10 left-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center relative z-10 p-6">
        <div className="w-full max-w-md">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/50">
            {/* Logo & Title */}
            <div className="flex flex-col items-center mb-6">
              <div className="w-16 h-16 rounded-full overflow-hidden shadow-lg border-4 border-indigo-100 mb-3">
                <img src="/logo.jpeg" alt="Logo" className="w-full h-full object-cover" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Forgot Password</h2>
              <p className="text-gray-500 text-sm mt-1">
                {step === 1 && 'Enter your email to receive a reset OTP'}
                {step === 2 && `Enter the OTP sent to ${hint || email}`}
                {step === 3 && 'Set your new password'}
              </p>
            </div>

            {/* Step indicators */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step >= s ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>{s}</div>
                  {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>}
                </div>
              ))}
            </div>

            {error && (
              <div className={`border-l-4 rounded-lg px-4 py-3 text-sm mb-4 ${
                error.includes('license') || error.includes('License') || error.includes('expired') || error.includes('Expired')
                  ? 'bg-red-50 border-red-500 text-red-700'
                  : 'bg-red-50 border-red-500 text-red-700'
              }`}>
                <div className="font-semibold mb-1">
                  {error.includes('license') || error.includes('License') || error.includes('expired') || error.includes('Expired') 
                    ? '🔐 License Issue' 
                    : '⚠️ Error'}
                </div>
                <div>{error}</div>
                {(error.includes('license') || error.includes('License') || error.includes('expired') || error.includes('Expired')) && (
                  <div className="mt-2 text-xs opacity-90">
                    Please contact your school administrator to renew the license.
                  </div>
                )}
              </div>
            )}
            {success && step !== 1 && (
              <div className="bg-green-50 border-l-4 border-green-500 text-green-700 px-4 py-3 rounded-lg text-sm mb-4">{success}</div>
            )}

            {/* Step 1: Email */}
            {step === 1 && (
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📧</span>
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-gray-50"
                      placeholder="Enter your registered email" />
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                  Available for <strong>Principal</strong> and <strong>Parent</strong> accounts only. Teachers should contact the admin.
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 shadow-lg">
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </form>
            )}

            {/* Step 2: OTP */}
            {step === 2 && (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                {noEmail && displayOtp && (
                  <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 text-center">
                    <p className="text-xs text-yellow-700 font-medium mb-1">⚠️ Email service not configured</p>
                    <p className="text-sm text-yellow-800 mb-2">Your OTP code is:</p>
                    <div className="bg-white rounded-lg py-3 px-4 inline-block shadow-inner border border-yellow-300">
                      <span className="text-3xl font-mono font-bold text-indigo-700 tracking-[0.3em]">{displayOtp}</span>
                    </div>
                    <p className="text-xs text-yellow-600 mt-2">Copy this code and enter it below. Valid for 15 minutes.</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Enter OTP</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔑</span>
                    <input type="text" required value={otp} onChange={(e) => setOtp(e.target.value)}
                      maxLength={6} pattern="[0-9]{6}"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-gray-50 text-center tracking-[0.5em] text-lg font-mono"
                      placeholder="• • • • • •" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-center">
                  {noEmail ? 'Enter the OTP displayed above' : 'OTP is valid for 15 minutes. Check your email or console.'}
                </p>
                <button type="submit" disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 shadow-lg">
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
                <button type="button" onClick={() => { setStep(1); setError(''); setSuccess(''); }}
                  className="w-full py-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                  ← Change Email
                </button>
              </form>
            )}

            {/* Step 3: New Password */}
            {step === 3 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔒</span>
                    <input type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      minLength={6}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-gray-50"
                      placeholder="Minimum 6 characters" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔒</span>
                    <input type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      minLength={6}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-gray-50"
                      placeholder="Re-enter password" />
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 shadow-lg">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            )}

            <div className="mt-6 text-center">
              <a href="/login" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">← Back to Login</a>
            </div>
          </div>

          <p className="text-center text-xs text-blue-200 mt-6">
            {schoolInfo.schoolName || 'School Management System'} &copy; {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
